import React, { useMemo, useState } from "react";
import ProposalRow from "./ProposalRow";
import ProposalDialog from "./ProposalDialog";
import Icon from "./Icon";
import { STATUS_FILTERS, countByStatus, filterProposals } from "./proposalsModel";
import "./proposals.css";

const SKELETON_ROWS = 3;

function Skeleton() {
  return (
    <div className="card list" aria-busy="true" aria-label="Loading proposals">
      {Array.from({ length: SKELETON_ROWS }).map((_, i) => (
        <div key={i} className="skrow"><span className="sk" style={{ width: 44 }} /><span className="sk" style={{ width: 200 + i * 40 }} /></div>
      ))}
    </div>
  );
}

/**
 * Instructor "Course proposals" list: header, status filter, search, sort, one row per proposal and a detail dialog.
 * `actions` supplies { onSubmitNew, onRefresh, onWithdraw, onRevise, onSchedule }; each row/dialog action receives the submission.
 */
export default function ProposalsView({ submissions, loading, error, onDismissError, actions }) {
  const [status, setStatus] = useState("all");
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState("newest");
  const [detail, setDetail] = useState(null);

  const counts = useMemo(() => countByStatus(submissions), [submissions]);
  const visible = useMemo(() => filterProposals(submissions, { status, query, sort }), [submissions, status, query, sort]);
  // Keep the dialog in sync with refreshed data (e.g. after a withdraw) rather than showing a stale copy.
  const open = detail ? submissions.find((s) => s.id === detail.id) || null : null;

  const withdraw = async (sub) => { setDetail(null); await actions.onWithdraw(sub); };
  const revise = (sub) => { setDetail(null); actions.onRevise(sub); };
  const schedule = (sub) => { setDetail(null); actions.onSchedule(sub); };

  return (
    <div className="pp-root">
      <div className="hd">
        <div><h1>Course Proposals</h1><p>Track the courses you've submitted and see where each one is in review.</p></div>
        <div className="hd-acts">
          <button type="button" className="btn btn-o" onClick={actions.onRefresh} disabled={loading} aria-label="Refresh proposals"><Icon n="refresh" s={14} /></button>
          <button type="button" className="btn btn-p" onClick={actions.onSubmitNew}><Icon n="plus" s={14} w={2.4} />Submit New Proposal</button>
        </div>
      </div>

      {error && <div className="warn" role="alert">{error} <button type="button" className="btn btn-g btn-sm" onClick={onDismissError}>Dismiss</button></div>}

      <div className="bar">
        <div className="seg" role="group" aria-label="Status">
          {STATUS_FILTERS.map(([key, label]) => (
            <button key={key} type="button" className={status === key ? "on" : ""} aria-pressed={status === key} onClick={() => setStatus(key)}>
              {label} <span className="cnt">{counts[key]}</span>
            </button>
          ))}
        </div>
        <label className="srch">
          <Icon n="search" s={15} />
          <input type="search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search by title or ID" aria-label="Search proposals" />
        </label>
        <select value={sort} onChange={(e) => setSort(e.target.value)} aria-label="Sort">
          <option value="newest">Newest first</option>
          <option value="oldest">Oldest first</option>
        </select>
      </div>

      {loading ? <Skeleton /> : visible.length === 0 ? (
        <div className="card empty">
          {query.trim() ? `No proposals match “${query.trim()}”.` : submissions.length === 0 ? "No proposals yet. Submit your first course proposal to get started." : "No proposals with this status."}
        </div>
      ) : (
        <div className="card list">
          {visible.map((sub) => (
            <ProposalRow key={sub.id} sub={sub} onOpen={setDetail} onSchedule={actions.onSchedule} onWithdraw={actions.onWithdraw} onRevise={actions.onRevise} />
          ))}
        </div>
      )}

      <ProposalDialog sub={open} onClose={() => setDetail(null)} onWithdraw={withdraw} onRevise={revise} onSchedule={schedule} />
    </div>
  );
}
